import { useState, useEffect, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import ComingSoonPopup from './ComingSoonPopup';
import CreateProjectAssets from './CreateProjectAssets';
import { DisplayMode } from '../models/project';
import { ProvidedAsset } from '../services/projectAssets';
import { IconSprite, IconCube, IconPhone, IconPhoneL, IconDesktop, IconCheck, IconSpark } from './Icons';
import { useIsMobile } from '../hooks/useIsMobile';

export interface ProjectSetupData {
  name: string;
  description: string;
  type: '2D' | '3D';
  displayMode: DisplayMode;
  providedAssets: ProvidedAsset[];
}

interface ProjectSettingsProps {
  onSubmit: (data: ProjectSetupData) => void;
  initialData?: Partial<ProjectSetupData>;
  submitButtonText?: string;
  existingProjectNames?: string[];
  isSubmitting?: boolean;
  canUploadAssets?: boolean;
}

const MAX_DESCRIPTION = 2000;

export default function ProjectSettings({
  onSubmit,
  initialData,
  submitButtonText,
  existingProjectNames = [],
  isSubmitting = false,
  canUploadAssets = false,
}: ProjectSettingsProps) {
  const { t } = useTranslation(['common']);
  const isMobile = useIsMobile();
  const [formData, setFormData] = useState<ProjectSetupData>({
    name: initialData?.name || '',
    description: initialData?.description || '',
    type: initialData?.type || '2D',
    displayMode: initialData?.displayMode || 'PC',
    providedAssets: initialData?.providedAssets || [],
  });
  const [nameError, setNameError] = useState<string>('');
  const [showComingSoonPopup, setShowComingSoonPopup] = useState(false);

  useEffect(() => {
    if (initialData) {
      setFormData({
        name: initialData.name || '',
        description: initialData.description || '',
        type: initialData.type || '2D',
        displayMode: initialData.displayMode || 'PC',
        providedAssets: initialData.providedAssets || [],
      });
    }
  }, [initialData]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (formData.name.trim() && !nameError) {
      onSubmit(formData);
    }
  };

  const setField = (field: keyof ProjectSetupData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (field === 'name') {
      const trimmed = value.trim();
      if (trimmed && existingProjectNames.some((n) => n.toLowerCase() === trimmed.toLowerCase())) {
        setNameError(t('projectSettings.nameTaken', 'A project with this name already exists.'));
      } else {
        setNameError('');
      }
    }
  };

  const Opt = ({
    selected,
    disabled,
    onClick,
    icon,
    title,
    sub,
    meta,
    column,
  }: {
    selected?: boolean;
    disabled?: boolean;
    onClick?: () => void;
    icon: ReactNode;
    title: string;
    sub?: string;
    meta?: ReactNode;
    column?: boolean;
  }) => (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={'opt' + (selected ? ' selected' : '') + (disabled ? ' disabled' : '')}
      style={
        column
          ? { flexDirection: 'column', alignItems: 'flex-start', gap: 10, padding: '14px 12px' }
          : undefined
      }
    >
      <span className="opt-ico">{icon}</span>
      <div>
        <div className="opt-title">{title}</div>
        {sub && <div className="opt-sub">{sub}</div>}
      </div>
      {meta && <span className="opt-meta">{meta}</span>}
      {selected && !meta && (
        <span className="opt-meta">
          <IconCheck />
        </span>
      )}
    </button>
  );

  return (
    <div style={{ width: '100%', maxWidth: 720, display: 'flex', flexDirection: 'column', gap: 28 }}>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 28 }}>
        <div className="field">
          <label htmlFor="ps-name">{t('projectSettings.name', 'Project name')}</label>
          <input
            id="ps-name"
            type="text"
            className={'input' + (nameError ? ' invalid' : '')}
            placeholder="Neon Drift"
            value={formData.name}
            onChange={(e) => setField('name', e.target.value)}
            required
          />
          {nameError && (
            <div style={{ color: 'var(--danger)', fontSize: 12 }}>{nameError}</div>
          )}
        </div>

        <div className="field">
          <label htmlFor="ps-description">{t('projectSettings.pitch', 'Pitch')}</label>
          <textarea
            id="ps-description"
            className="textarea"
            style={{ minHeight: 140 }}
            placeholder={t(
              'projectSettings.pitchPlaceholder',
              'Describe your game in plain language. Genre, mood, references, mechanics — anything that helps tell the story.',
            )}
            maxLength={MAX_DESCRIPTION}
            value={formData.description}
            onChange={(e) => setField('description', e.target.value)}
            required
          />
          <div className="muted" style={{ fontSize: 12, textAlign: 'right' }}>
            {formData.description.length} / {MAX_DESCRIPTION}
          </div>
        </div>

        <div>
          <label
            style={{
              display: 'block',
              font: '500 12px/1 var(--font-sans)',
              color: 'var(--text-2)',
              marginBottom: 10,
            }}
          >
            {t('projectSettings.engine', 'Engine')}
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10 }}>
            <Opt
              selected={formData.type === '2D'}
              onClick={() => setField('type', '2D')}
              icon={<IconSprite />}
              title="2D"
              sub={t('projectSettings.engine2d', 'Sprite-based games — top-down, side-scrollers, puzzles.')}
            />
            <Opt
              disabled
              onClick={() => setShowComingSoonPopup(true)}
              icon={<IconCube />}
              title="3D"
              sub={t('projectSettings.engine3d', 'Full 3D worlds with cameras and physics.')}
              meta={
                <span className="chip warn">
                  <span className="dot" />
                  {t('projectSettings.soon', 'Soon')}
                </span>
              }
            />
          </div>
        </div>

        <div>
          <label
            style={{
              display: 'block',
              font: '500 12px/1 var(--font-sans)',
              color: 'var(--text-2)',
              marginBottom: 10,
            }}
          >
            {t('projectSettings.display', 'Display')}
          </label>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr', gap: 10 }}>
            <Opt
              column
              selected={formData.displayMode === 'MobilePortrait'}
              onClick={() => setField('displayMode', 'MobilePortrait')}
              icon={<IconPhone />}
              title={t('projectSettings.mobilePortrait', 'Mobile Portrait')}
            />
            <Opt
              column
              selected={formData.displayMode === 'MobileLandscape'}
              onClick={() => setField('displayMode', 'MobileLandscape')}
              icon={<IconPhoneL />}
              title={t('projectSettings.mobileLandscape', 'Mobile Landscape')}
            />
            <Opt
              column
              selected={formData.displayMode === 'PC'}
              onClick={() => setField('displayMode', 'PC')}
              icon={<IconDesktop />}
              title="PC"
            />
          </div>
        </div>

        <CreateProjectAssets
          value={formData.providedAssets}
          onChange={(providedAssets) => setFormData((prev) => ({ ...prev, providedAssets }))}
          enabled={canUploadAssets}
        />

        <button
          type="submit"
          disabled={
            isSubmitting ||
            !formData.name.trim() ||
            !formData.description.trim() ||
            !!nameError
          }
          className="btn primary lg"
          style={{ width: '100%', justifyContent: 'center' }}
        >
          {isSubmitting
            ? t('projectSettings.generating', 'Generating…')
            : submitButtonText || t('projectSettings.generate', 'Generate first build')}
          <IconSpark />
        </button>
      </form>

      <ComingSoonPopup
        isOpen={showComingSoonPopup}
        onClose={() => setShowComingSoonPopup(false)}
        title={t('projectSettings.comingSoon3dTitle', '3D game development is on the way')}
        message={t(
          'projectSettings.comingSoon3dMessage',
          "We're working on full 3D worlds with cameras and physics. For now, enjoy building 2D games.",
        )}
      />
    </div>
  );
}
